import api from "../../lib/api";
import { useEffect, useState, useCallback } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "../ui/table";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useToast } from "../../hooks/use-toast";
import { PRODUCT_TYPES } from "../../lib/constants";
import { CustomSelect } from "../ui/custom-select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import Pagination from "../ui/pagination";
import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { Loader } from "../layout/Loader";
import { DownloadIcon, X, FileText, Search } from "lucide-react";
import { useAuth } from "../../lib/authContext";

type Tab = "unmapped" | "mapped";

export default function MedsCategoryList() {
  const [medsCategory, setMedsCategory] = useState([]);
  const [currentEdit, setCurrentEdit] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [activeTab, setActiveTab] = useState<Tab>("unmapped");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const { toast } = useToast();
  const { excelPermissions } = useAuth();

  const getMedsCategory = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.getMedsCategoryList(
        currentPage,
        limit,
        search || undefined,
        activeTab,
      );
      setMedsCategory(response.data || []);
      setTotalPages(Math.ceil((response.totalCount || 0) / limit));
    } catch {
      setMedsCategory([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, limit, search, activeTab]);

  useEffect(() => {
    getMedsCategory();
  }, [getMedsCategory]);

  // Reset page when switching tabs or searching
  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setCurrentEdit(null);
  };

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setCurrentPage(1);
  };

  async function saveMedsCategory() {
    if (
      !currentEdit.product_type ||
      currentEdit.product_type === "product_type"
    ) {
      toast({
        title: "Product type is required",
        variant: "destructive",
      });
      return;
    }
    const response = await api.updateMedsCategory(currentEdit);
    if (response.success) {
      toast({
        title: response.message,
      });
      setCurrentEdit(null);
      getMedsCategory();
    }
  }

  async function deleteMedsCategory(id: number) {
    if (window.confirm("Are you sure you want to delete this meds category?")) {
      const response = await api.deleteMedsCategory(id);
      if (response.success) {
        toast({
          title: response.message,
        });
        getMedsCategory();
      } else {
        toast({
          title: response.error || "Error deleting meds category",
          variant: "destructive",
        });
      }
    }
  }

  async function getLatestRequestId(product_id: string) {
    const response = await api.getLatestRequestId(product_id);
    if (response.success) {
      try {
        await navigator.clipboard.writeText(response.data);
        toast({
          title: `Request ID copied to clipboard: ${response.data}`,
        });
      } catch {
        const textArea = document.createElement("textarea");
        textArea.value = response.data;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast({
          title: `Request ID copied to clipboard: ${response.data}`,
        });
      }
    } else {
      toast({
        title: response.error || "Error fetching latest request ID",
        variant: "destructive",
      });
    }
  }

  async function downloadExcel() {
    const response = await api.downloadMedsCategoryExcel();
    if (response.success) {
      const link = document.createElement("a");
      link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data}`;
      link.download = response.filename;
      link.click();
    }
  }

  const renderRow = (category: any) => {
    const isEditing = currentEdit?.id === category.id;

    return (
      <TableRow key={category.id} className="dark:border-gray-700">
        <TableCell className="dark:text-white dark:bg-gray-800">
          {category.product_id}
        </TableCell>
        <TableCell className="dark:text-white dark:bg-gray-800 w-1/6">
          {category.product_name}
        </TableCell>
        {isEditing ? (
          <>
            <TableCell className="dark:text-white dark:bg-gray-800 w-1/6">
              <CustomSelect
                options={[
                  { label: "Select product type", value: "product_type" },
                  ...PRODUCT_TYPES.map((type) => ({
                    label: type,
                    value: type,
                  })),
                ]}
                placeholder="Select product type"
                className="w-[200px]"
                value={
                  currentEdit?.product_type ||
                  category.product_type ||
                  "product_type"
                }
                onChange={(value) =>
                  setCurrentEdit({
                    ...currentEdit,
                    product_type: value,
                  })
                }
              />
            </TableCell>
            <TableCell className="dark:text-white dark:bg-gray-800 w-1/6">
              <Input
                value={currentEdit?.product_packing ?? category.product_packing ?? ""}
                onChange={(e) =>
                  setCurrentEdit({
                    ...currentEdit,
                    product_packing: e.target.value,
                  })
                }
              />
            </TableCell>
            <TableCell className="dark:text-white dark:bg-gray-800 w-1/6">
              <Input
                value={
                  currentEdit?.product_manufacturer ??
                  category.product_manufacturer ??
                  ""
                }
                onChange={(e) =>
                  setCurrentEdit({
                    ...currentEdit,
                    product_manufacturer: e.target.value,
                  })
                }
              />
            </TableCell>
            <TableCell className="dark:text-white dark:bg-gray-800 w-1/6">
              {!currentEdit.product_type ||
              currentEdit.product_type === "product_type" ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="cursor-not-allowed">
                      <Button
                        className="bg-black text-white dark:bg-white dark:text-black"
                        disabled={true}
                      >
                        Save
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Please select product type
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Button
                  className="bg-black text-white dark:bg-white dark:text-black"
                  onClick={() => saveMedsCategory()}
                >
                  Save
                </Button>
              )}
              <Button
                className="ml-2 bg-red-500 text-white dark:bg-red-500 dark:text-white"
                onClick={() => setCurrentEdit(null)}
              >
                Cancel
              </Button>
            </TableCell>
          </>
        ) : (
          <>
            <TableCell className="dark:text-white dark:bg-gray-800 w-1/6">
              {category.product_type || (
                <span className="text-gray-400 italic">Not set</span>
              )}
            </TableCell>
            <TableCell className="dark:text-white dark:bg-gray-800 w-1/6">
              {category.product_packing}
            </TableCell>
            <TableCell className="dark:text-white dark:bg-gray-800 w-1/6">
              {category.product_manufacturer}
            </TableCell>
            <TableCell className="dark:text-white dark:bg-gray-800 w-1/6">
              <div className="flex gap-2">
                <Button
                  className="bg-black text-white dark:bg-white dark:text-black"
                  onClick={() =>
                    setCurrentEdit({ ...category, id: category.id })
                  }
                >
                  Edit
                </Button>
                <Button
                  className="bg-red-500 text-white dark:bg-red-500 dark:text-white"
                  onClick={() => deleteMedsCategory(category.id)}
                  size="sm"
                >
                  <X className="w-4 h-4" />
                </Button>
                <Button
                  className="bg-blue-500 text-white dark:bg-blue-600 dark:text-white"
                  onClick={() => getLatestRequestId(category.product_id)}
                  size="sm"
                >
                  <FileText className="w-4 h-4" />
                </Button>
              </div>
            </TableCell>
          </>
        )}
      </TableRow>
    );
  };

  return (
    <div className="container mx-auto p-4 bg-white rounded-md shadow-md dark:bg-gray-800">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Meds Category</h1>
        {excelPermissions.canDownloadExcel && (
          <Button
            className="bg-green-500 text-white dark:bg-white dark:text-black"
            onClick={() => downloadExcel()}
          >
            Download Excel <DownloadIcon className="ml-2 w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-4 flex gap-1 border-b dark:border-gray-700">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "unmapped"
              ? "border-black text-black dark:border-white dark:text-white"
              : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
          }`}
          onClick={() => switchTab("unmapped")}
        >
          Unmapped
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "mapped"
              ? "border-black text-black dark:border-white dark:text-white"
              : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
          }`}
          onClick={() => switchTab("mapped")}
        >
          Mapped
        </button>
      </div>

      {/* Search */}
      <div className="mt-4 flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name or ID..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="pl-9"
          />
        </div>
        <Button
          onClick={handleSearch}
          className="bg-black text-white dark:bg-white dark:text-black"
        >
          Search
        </Button>
        {search && (
          <Button variant="outline" onClick={clearSearch}>
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="mt-4 border rounded-md dark:border-gray-700">
        {isLoading ? (
          <div className="flex justify-center items-center h-full w-full p-4">
            <Loader />
          </div>
        ) : (
          <Table className="w-full">
            <TableHeader>
              <TableRow className="dark:text-white dark:bg-gray-800 dark:border-gray-700">
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Packing</TableHead>
                <TableHead>Manufacturer</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {medsCategory && medsCategory.length > 0 ? (
                medsCategory.map((category: any) => renderRow(category))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    {search
                      ? `No products found matching "${search}"`
                      : activeTab === "unmapped"
                        ? "No unmapped products"
                        : "No mapped products"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-4 flex justify-between">
        <div className="w-1/6">
          <Select
            value={limit.toString()}
            onValueChange={(value) => setLimit(Number(value))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select limit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500 mt-2">items per page</span>
        </div>
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
